import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonRow,
    IonCol,
    IonText,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}

const getRandomTime = () => {
    const hours = Math.floor(Math.random() * 12) + 1; // 1 to 12
    const minutes = Math.floor(Math.random() * 12) * 5; // 0, 5, 10, ..., 55
    return { hours, minutes };
};

// Format time as "hh:mm"
const formatTime = ({ hours, minutes }: { hours: number; minutes: number }) => {
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
};

// Generate 3 incorrect answers that are different from the correct one
const generateOptions = (correctTime: string) => {
    const options = new Set<string>();
    options.add(correctTime);

    while (options.size < 3) {
        const randomTime = formatTime(getRandomTime());
        if (!options.has(randomTime)) {
            options.add(randomTime);
        }
    }
    return Array.from(options).sort(() => Math.random() - 0.5); // Shuffle
};

const getRandomInt = (min: number, max: number): number =>
    Math.floor(Math.random() * (max - min + 1)) + min;

interface QuestionData {
    question: string;
    options: (number | string)[];
    answer: number | string;
  }

const fulldays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const generateCalendar = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();

    const calendarDays = Array(firstDay).fill(null);
    for (let i = 1; i <= totalDays; i++) {
        calendarDays.push(i);
    }
    return calendarDays;
};

const getOrdinalSuffix = (n: number) => {
    if (n >= 11 && n <= 13) return "th";
    const lastDigit = n % 10;
    if (lastDigit === 1) return "st";
    if (lastDigit === 2) return "nd";
    if (lastDigit === 3) return "rd";
    return "th";
};


const generateRandomQuestion = (year: number, month: number) => {
    const questionTypes = [
        "first_sunday",
        "last_saturday",
        "random_weekday",
        "count_days",
        "birthday_relative_to_weekday",
        "find_weekday_of_date",
        "weekday_after_date",
        "nth_weekday_after_nth_day"
    ];

    const selectedType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
    const calendarDays = generateCalendar(year, month); //console.log(calendarDays)
    let question = "", answer = 0;
    const randomDay = Math.floor(Math.random() * 7); console.log(randomDay);

    console.log(selectedType)

    switch (selectedType) {
        case "first_sunday": {
            answer = calendarDays.find((_, i) => i % 7 === 0 && calendarDays[i] != null); console.log(answer)
            question = "Which date is the first Sunday of the month?";
            break;
        }
        case "last_saturday": {
            const lastSaturday = calendarDays.reverse().find((_, i) => (calendarDays.length - 1 - i) % 7 === 6 && calendarDays[i] != null);
            answer = lastSaturday || 1;
            question = "Which date is the last Saturday of the month?";
            break;
        }
        case "random_weekday": {
            const weekdayIndex = Math.floor(Math.random() * 7); // Randomly pick a weekday (0 = Sunday, 6 = Saturday)
            const occurrence = Math.floor(Math.random() * 4) + 1; // Randomly pick 1st, 2nd, 3rd, or 4th occurrence
            let count = 0;
            let selectedDate = null;

            for (let i = 0; i < calendarDays.length; i++) {
                if (calendarDays[i] !== null && new Date(year, month, calendarDays[i]).getDay() === randomDay) {
                    count++;
                    if (count === occurrence) {
                        selectedDate = calendarDays[i];
                        break;
                    }
                }
            }

            answer = selectedDate || 1;
            const ordinal = ["first", "second", "third", "fourth"][occurrence - 1];
            question = `On which date does the ${ordinal} ${fulldays[randomDay]} fall?`;
            break;
        }

        case "count_days": {
            console.log(calendarDays)
            console.log(calendarDays.filter((_, i) => i % 7 === randomDay))
            question = `How many ${fulldays[randomDay]}s are there in this month?`;
            answer = calendarDays.filter((_, i) => i % 7 === randomDay && calendarDays[i] != null).length; console.log(randomDay, answer)
            break;
        }

        case "birthday_relative_to_weekday": {
            const weekday = getRandomInt(0, 6); // 0=Sun, 6=Sat
            const ordinal = getRandomInt(1, 4); // 1st to 4th
            const offset = getRandomInt(1, 5); // 1 to 5 days before or after
            const before = Math.random() < 0.5;

            // Find Nth occurrence of weekday
            let count = 0;
            let targetDate: number | null = null;
            for (let i = 0; i < calendarDays.length; i++) {
                const day = calendarDays[i];
                if (day && new Date(year, month, day).getDay() === weekday) {
                    count++;
                    if (count === ordinal) {
                        targetDate = day;
                        break;
                    }
                }
            }

            if (!targetDate) break;

            const birthdayDate = before ? targetDate - offset : targetDate + offset;

            if (birthdayDate < 1 || birthdayDate > 31) break; // skip invalid

            question = `If someone's birthday is ${offset} day${offset > 1 ? "s" : ""} ${before ? "before" : "after"} the ${["first", "second", "third", "fourth"][ordinal - 1]} ${fulldays[weekday]}, what is the date?`;
            answer = birthdayDate;
            break;
        }

        case "find_weekday_of_date": {
            const day = getRandomInt(1, new Date(year, month + 1, 0).getDate());
            const d = new Date(year, month, day);
            question = `What day of the week is the ${day}${getOrdinalSuffix(day)} of this month?`;
            answer = d.getDay(); // index
            const optionsSet = new Set<number>([answer]);

            while (optionsSet.size < 4) optionsSet.add(getRandomInt(0, 6));
            const dayOptions = Array.from(optionsSet)
                .map(d => fulldays[d])
                .sort(() => Math.random() - 0.5);

            return { question, options: dayOptions, answer: fulldays[answer] };
        }

        case "weekday_after_date": {
            const targetWeekday = getRandomInt(0, 6); // 0=Sunday to 6=Saturday
            const startDate = getRandomInt(15, 22); // Avoid too close to month end
            let resultDate = null;

            for (let i = startDate + 1; i <= calendarDays.length; i++) {
                if (calendarDays.includes(i)) {
                    const day = new Date(year, month, i).getDay();
                    if (day === targetWeekday) {
                        resultDate = i;
                        break;
                    }
                }
            }

            if (!resultDate) break;

            question = `Which date after ${startDate}${getOrdinalSuffix(startDate)} is a ${fulldays[targetWeekday]}?`;
            answer = resultDate;
            break;
        }


        case "nth_weekday_after_nth_day": {
            const baseDate = getRandomInt(5, 20); // Avoid too early or too late
            const weekday = getRandomInt(0, 6);   // Target weekday
            const occurrence = getRandomInt(1, 3); // 1st, 2nd or 3rd occurrence

            let count = 0;
            let foundDate: number | null = null;

            for (let i = baseDate + 1; i <= calendarDays.length; i++) {
                if (!calendarDays.includes(i)) continue;
                const dayOfWeek = new Date(year, month, i).getDay();
                if (dayOfWeek === weekday) {
                    count++;
                    if (count === occurrence) {
                        foundDate = i;
                        break;
                    }
                }
            }

            if (!foundDate) break;

            question = `What date is the ${["first", "second", "third"][occurrence - 1]} ${fulldays[weekday]} after the ${baseDate}${getOrdinalSuffix(baseDate)}?`;
            answer = foundDate;
            break;
        }



        default:
            break;
    }

    const options = [answer];
    while (options.length < 4) {
        const randomOption = Math.floor(Math.random() * 31) + 1;
        if (!options.includes(randomOption)) options.push(randomOption);
    }
    return { question, options: options.sort(() => Math.random() - 0.5), answer };
};

interface Props {
    onComplete?: () => void;
}

const Time: React.FC<Props> = ({ onComplete }) => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [time, setTime] = useState(getRandomTime());
    const [options, setOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [feedback, setFeedback] = useState("");
    const [selectedType, setSelectedType] = useState(0);
    const today = new Date();
    const randomYear = Math.floor(Math.random() * (today.getFullYear() - 2000 + 1)) + 2000;
    const randomMonth = Math.floor(Math.random() * 12);

    const [year, setYear] = useState(randomYear);
    const [month, setMonth] = useState(randomMonth);
    const [calendarDays, setCalendarDays] = useState<number[]>([]);
    const [questionData, setQuestionData] = useState<QuestionData | null>();
    const [message, setMessage] = useState("");

    const loadTimeQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeMathematics/Time.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeMathematics/Time.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    onComplete?.()
                }
                else {
                    setCurrentIndex(0);
                }
                createNewQuestion();
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    const createNewQuestion = async () => {
        const patternType = getRandomInt(0, 2);
        setSelectedType(patternType);

        const q: Question[] = [];
        if (patternType === 0) {
            await loadTimeQuestions(); // this already sets state
        } else if (patternType === 1) {
            let r;
        }
        else {

            setCalendarDays(generateCalendar(year, month));
            setQuestionData(generateRandomQuestion(year, month));
        }
    };

    useEffect(() => {
        createNewQuestion();

        document.title =
            "Mamas Guidance - Class Three - Mathematics";
    }, []);

    useEffect(() => {
        const correctTime = formatTime(time);
        setOptions(generateOptions(correctTime));
    }, [time]);

    const handleOptionClick = (option: string) => {
        setSelected(option);
        if (option === formatTime(time)) {
            setShowImage(true);
            setTimeout(() => {
                createNewQuestion()
                setTime(getRandomTime());
                setShowImage(false);
                setFeedback("")
                onComplete?.()
            }, 5000)
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    const handleCalenderOptionClick = (selectedOption: any) => {
        const element = document.getElementsByClassName("messageArea")[0];
        element.scrollIntoView({
            behavior: 'smooth', // Smooth scrolling animation
            block: 'center',    // Align the item in the center of the viewport
        });
        setShowImage(true);
        if (selectedOption === questionData?.answer) {
            setTimeout(() => {
                setShowImage(false);
                createNewQuestion()
                setFeedback("")
                onComplete?.()
            }, 5000);
        } else {
            setMessage("❌ Wrong answer! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Prepare for Class 1 IGKO Olympiad with Mama's Guidance. Access syllabus details, sample questions, tips, and practice worksheets to boost your child's performance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    {currentQuestion ? (
                        <>
                            {selectedType === 0 ? (
                                <>
                                    <h3 className="mb-3">{currentQuestion.question}</h3>
                                    {(currentQuestion.type === "multiple-choice" ||
                                        currentQuestion.type === "fill-in-the-blank") && (
                                            <IonRadioGroup
                                                onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                            >
                                                <IonList>
                                                    {(currentQuestion.options || []).map((option, index) => (
                                                        <IonItem
                                                            className={`grid-item ${selectedOption === option
                                                                ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                                    ? "correct"
                                                                    : "errorItems"
                                                                : ""
                                                                }`}
                                                            key={index}
                                                        >
                                                            <IonRadio slot="start" value={option} />
                                                            <IonLabel>{option}</IonLabel>
                                                        </IonItem>
                                                    ))}
                                                </IonList>
                                            </IonRadioGroup>
                                        )}
                                    {currentQuestion.type === "true-false" && (
                                        <IonRadioGroup
                                            onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                        >
                                            <IonList>
                                                {["true", "false"].map((option, index) => (
                                                    <IonItem key={index} className={`${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}>
                                                        <IonRadio slot="start" value={option} />
                                                        <IonLabel>{option.toUpperCase()}</IonLabel>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        </IonRadioGroup>
                                    )}
                                </>
                            ) : selectedType === 1 ? (
                                <>


                                    {/* Clock Display */}
                                    <div
                                        style={{
                                            width: "200px",
                                            height: "200px",
                                            borderRadius: "50%",
                                            border: "5px solid black",
                                            margin: "20px auto",
                                            position: "relative",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            backgroundColor: "white",
                                        }}
                                    >
                                        {/* Clock Numbers */}
                                        {[...Array(12)].map((_, i) => {
                                            const angle = (i + 1) * 30;
                                            const x = 85 + 80 * Math.sin((angle * Math.PI) / 180);
                                            const y = 85 - 80 * Math.cos((angle * Math.PI) / 180);

                                            return (
                                                <div
                                                    key={i}
                                                    style={{
                                                        position: "absolute",
                                                        left: `${x}px`,
                                                        top: `${y}px`,
                                                        fontSize: "16px",
                                                        fontWeight: "bold",
                                                    }}
                                                >
                                                    {i + 1}
                                                </div>
                                            );
                                        })}

                                        {/* Hour Hand */}
                                        <div
                                            style={{
                                                width: "5px",
                                                height: "50px",
                                                backgroundColor: "black",
                                                position: "absolute",
                                                left: "50%",
                                                bottom: "50%",
                                                transformOrigin: "bottom",
                                                transform: `rotate(${(time.hours % 12) * 30 + (time.minutes / 60) * 30}deg)`,
                                            }}
                                        />

                                        {/* Minute Hand */}
                                        <div
                                            style={{
                                                width: "3px",
                                                height: "65px",
                                                backgroundColor: "black",
                                                position: "absolute",
                                                left: "50%",
                                                bottom: "50%",
                                                transformOrigin: "bottom",
                                                transform: `rotate(${time.minutes * 6}deg)`,
                                            }}
                                        />
                                    </div>

                                    {/* Answer Options */}
                                    <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
                                        {options.map((option, index) => (
                                            <div className="purpleButton"
                                                key={index}
                                                onClick={() => handleOptionClick(option)}
                                                style={{ margin: "5px", padding: "10px", minWidth: "60px" }}
                                            >
                                                {option}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <>
                                    <h2 style={{ textAlign: "center" }}>
                                        {new Date(year, month).toLocaleString("default", { month: "long" })} {year}
                                    </h2>
                                    {/* Week Days Header */}
                                    <IonRow>
                                        {days.map((day, index) => (
                                            <IonCol key={index} className="ion-text-center">
                                                <IonText><strong>{day}</strong></IonText>
                                            </IonCol>
                                        ))}
                                    </IonRow>
                                    {/* Calendar Days */}
                                    {Array.from({ length: Math.ceil(calendarDays.length / 7) }).map((_, rowIndex) => {
                                        const weekDays = calendarDays.slice(rowIndex * 7, rowIndex * 7 + 7);
                                        //console.log(weekDays)
                                        return (
                                            <IonRow key={rowIndex}>
                                                {weekDays.map((day, index) => (
                                                    <IonCol
                                                        key={index}
                                                        className="ion-text-center"
                                                        style={{ minHeight: "40px", border: "1px solid #ccc" }}>
                                                        {day !== null ? <IonText>{day}</IonText> : <IonText>&nbsp;</IonText>}
                                                    </IonCol>
                                                ))}

                                                {/* Add extra empty cells if last row has less than 7 days */}
                                                {weekDays.length < 7 &&
                                                    Array.from({ length: 7 - weekDays.length }).map((_, i) => (
                                                        <IonCol key={`empty-${i}`} style={{ border: "1px solid #ccc", minHeight: "40px" }}>
                                                            &nbsp;
                                                        </IonCol>
                                                    ))}
                                            </IonRow>
                                        );
                                    })}

                                    <div style={{ textAlign: "center", marginTop: "20px", marginBottom: "20px" }}>
                                        <h3>{questionData?.question}</h3>
                                        <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap", marginTop: "20px" }}>
                                            {questionData?.options.map((option, index) => (
                                                <div className="purpleButton" key={index} onClick={() => handleCalenderOptionClick(option)} style={{ width: 'auto', padding: '10px', fontSize: '20px', minWidth: '60px' }}>
                                                    {option}
                                                </div>
                                            ))}
                                        </div>
                                        <p className="messageArea" style={{ fontWeight: "bold", marginTop: "10px", marginBottom: "40px" }}>{message}</p>
                                    </div>
                                </>
                            )}


                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default Time;
